// Prev/Next bill navigation shared by the View Billing sheet (billing.tsx) and
// the Edit Bill screen (createBill.tsx).
//
// Steps strictly by invoice NUMBER (e.g. INVCE-00000011052 -> ...11051 on
// Previous), not by the row's position in whatever filtered/sorted Customer
// Bills list happens to be loaded — so it behaves the same regardless of any
// active search/payment filter. A decremented/incremented number that doesn't
// correspond to an existing bill (gaps happen: online orders and in-store
// bills share one counter, and it's a plain MAX()+1, not a reserved sequence)
// is skipped over automatically until an existing bill is found.
//
// Each retry is a full sequential network round-trip (never parallel, so an
// unlucky run of gaps directly adds up in wall-clock time) — kept small so a
// long run of missing numbers gives up in a couple of seconds instead of
// hammering the API for a minute-plus and leaving the buttons looking dead.
import { useEffect, useRef, useState } from "react";

/** How far past a missing invoice number to keep looking before giving up. */
const MAX_LOOKAHEAD = 20;

interface ParsedInvoiceId {
    prefix: string;
    digits: number;
    width: number;
}

function parseInvoiceId(invoiceId: any): ParsedInvoiceId | null {
    const match = typeof invoiceId === "string" ? invoiceId.match(/^(.*?)(\d+)$/) : null;
    if (!match) return null;
    return { prefix: match[1], digits: Number(match[2]), width: match[2].length };
}

function formatInvoiceId(parsed: ParsedInvoiceId, digits: number): string {
    return `${parsed.prefix}${String(digits).padStart(parsed.width, "0")}`;
}

export interface UseBillNavigationArgs {
    /** Invoice id of the bill currently being viewed/edited. */
    currentInvoiceId: any;
    /** billNavigationRow — result of the last single-invoiceId probe (same
     *  shape as billingList.getBillingList: { billingDetails, page }). Kept on
     *  its own reducer slice so probing never clobbers the Customer Bills
     *  table's own list state. */
    probeResult: any;
    /** True while a probe request is in flight. */
    probeLoading: boolean;
    /** Dispatch a getBillingList-style lookup for this exact invoiceId. */
    onProbe: (invoiceId: string) => void;
    /** Open the given billing-list row (view it / navigate to its edit page). */
    onSelect: (row: any) => void;
    /** Called once when a direction gives up without finding a bill (e.g. to toast it). */
    onExhausted?: (direction: "prev" | "next") => void;
}

export interface BillNavigation {
    hasPrev: boolean;
    hasNext: boolean;
    goPrev: () => void;
    goNext: () => void;
    /** True while stepping through missing invoice numbers looking for the next existing one. */
    isPending: boolean;
}

export function useBillNavigation({
    currentInvoiceId,
    probeResult,
    probeLoading,
    onProbe,
    onSelect,
    onExhausted,
}: UseBillNavigationArgs): BillNavigation {
    const [probe, setProbe] = useState<{ direction: "prev" | "next"; digits: number; attempts: number } | null>(null);
    const [exhausted, setExhausted] = useState<{ prev: boolean; next: boolean }>({ prev: false, next: false });

    // Held in refs so the effect below doesn't re-run (and re-dispatch) every
    // time a parent re-render hands us fresh inline callbacks.
    const onSelectRef = useRef(onSelect);
    onSelectRef.current = onSelect;
    const onProbeRef = useRef(onProbe);
    onProbeRef.current = onProbe;
    const onExhaustedRef = useRef(onExhausted);
    onExhaustedRef.current = onExhausted;

    const parsed = parseInvoiceId(currentInvoiceId);

    // A new current bill (stepped to, or opened fresh) invalidates any
    // in-flight probe and resets the give-up state.
    const lastInvoiceIdRef = useRef(currentInvoiceId);
    if (lastInvoiceIdRef.current !== currentInvoiceId) {
        lastInvoiceIdRef.current = currentInvoiceId;
        if (probe !== null) setProbe(null);
        if (exhausted.prev || exhausted.next) setExhausted({ prev: false, next: false });
    }

    // Disabled (not just visually, but for real) while a probe is in flight —
    // otherwise an impatient re-click during a multi-step retry silently no-ops
    // (the guard in startProbe below), which reads as "the button is broken".
    const hasPrev = !!parsed && parsed.digits > 1 && !exhausted.prev && !probe;
    const hasNext = !!parsed && !exhausted.next && !probe;

    const startProbe = (direction: "prev" | "next") => {
        if (probe || !parsed) return;
        const digits = direction === "prev" ? parsed.digits - 1 : parsed.digits + 1;
        if (digits < 1) return;
        setProbe({ direction, digits, attempts: 1 });
        onProbeRef.current(formatInvoiceId(parsed, digits));
    };

    const goPrev = () => startProbe("prev");
    const goNext = () => startProbe("next");

    useEffect(() => {
        if (!probe || probeLoading || !parsed) return;
        const candidateInvoiceId = formatInvoiceId(parsed, probe.digits);
        const rows = Array.isArray(probeResult?.getBillingList?.billingDetails) ? probeResult.getBillingList.billingDetails : [];
        const match = rows.find((row: any) => String(row?.invoiceId) === candidateInvoiceId);

        if (match) {
            onSelectRef.current(match);
            setProbe(null);
            return;
        }

        const nextDigits = probe.direction === "prev" ? probe.digits - 1 : probe.digits + 1;
        if (nextDigits < 1 || probe.attempts >= MAX_LOOKAHEAD) {
            setProbe(null);
            setExhausted((prevState) => ({ ...prevState, [probe.direction]: true }));
            onExhaustedRef.current?.(probe.direction);
            return;
        }

        setProbe({ direction: probe.direction, digits: nextDigits, attempts: probe.attempts + 1 });
        onProbeRef.current(formatInvoiceId(parsed, nextDigits));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [probe, probeLoading, probeResult]);

    return {
        hasPrev,
        hasNext,
        goPrev,
        goNext,
        isPending: !!probe,
    };
}

export default useBillNavigation;
